import {
  __name,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-CRQHCYH4.js";

// src/hooks/use_debounce.ts
import { useEffect, useState } from "react";
var useDebounce = /* @__PURE__ */ __name((value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}, "useDebounce");

// src/hooks/use_title.ts
import { useEffect as useEffect2 } from "react";
var useTitle = /* @__PURE__ */ __name((...titleParts) => {
  useEffect2(() => {
    const previous = document.title;
    const appName = "SignAdmin";
    const usableParts = titleParts.filter((part) => part);
    if (usableParts.length > 0) {
      document.title = [usableParts.join(" \xB7 "), appName].join(" | ");
      return () => {
        document.title = previous;
      };
    }
    document.title = appName;
    return;
  }, [titleParts]);
}, "useTitle");

// src/hooks/use_media.tsx
import { useEffect as useEffect3, useState as useState2 } from "react";
var isBrowser = typeof window !== "undefined";
var getInitialState = /* @__PURE__ */ __name((query, defaultState) => {
  if (defaultState !== void 0 && !isBrowser) {
    return defaultState;
  }
  if (isBrowser) {
    return window.matchMedia(query).matches;
  }
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "`useMedia` When server side rendering, defaultState should be defined to prevent a hydration mismatches."
    );
  }
  return false;
}, "getInitialState");
var useMedia = /* @__PURE__ */ __name((query, defaultState) => {
  const [state, setState] = useState2(getInitialState(query, defaultState));
  useEffect3(() => {
    let mounted = true;
    const mql = window.matchMedia(query);
    const onChange = /* @__PURE__ */ __name(() => {
      if (!mounted) {
        return;
      }
      setState(!!mql.matches);
    }, "onChange");
    if (mql.addEventListener)
      mql.addEventListener("change", onChange, { passive: true });
    else
      mql.addListener(onChange);
    setState(mql.matches);
    return () => {
      mounted = false;
      if (mql.removeEventListener)
        mql.removeEventListener("change", onChange);
      else
        mql.removeListener(onChange);
    };
  }, [query]);
  return state;
}, "useMedia");
var use_media_default = useMedia;

// src/hooks/use_toast.ts
import { useCallback as useCallback2 } from "react";

// src/components/toast.tsx
import { useEffect as useEffect4, useLayoutEffect, useRef, useState as useState4 } from "react";
import cn3 from "classnames";

// src/context.tsx
import React, { useCallback, useMemo, useState as useState3 } from "react";

// src/theme.tsx
import Cookies from "universal-cookie";
var ThemeOption = /* @__PURE__ */ ((ThemeOption3) => {
  ThemeOption3[ThemeOption3["system"] = 0] = "system";
  ThemeOption3[ThemeOption3["light"] = 1] = "light";
  ThemeOption3[ThemeOption3["dark"] = 2] = "dark";
  return ThemeOption3;
})(ThemeOption || {});
var _ThemeManager = class _ThemeManager {
  constructor(storageName, ssr, defaultTheme) {
    this.changeListeners = [];
    this.changeHandler = /* @__PURE__ */ __name((ev) => {
      if (ev.key === this.storageName) {
        this.update(this.current(ev.newValue));
      }
    }, "changeHandler");
    this.ssr = ssr;
    this.storageName = storageName;
    this.defaultTheme = defaultTheme || 0 /* system */;
    this.cookies = new Cookies();
    if (typeof window !== "undefined")
      window.addEventListener("storage", this.changeHandler);
  }
  current(value) {
    if (this.ssr) {
      return this.defaultTheme;
    }
    switch (value || localStorage.getItem(this.storageName)) {
      case "dark":
        return 2 /* dark */;
      case "light":
        return 1 /* light */;
    }
    return this.defaultTheme;
  }
  update(theme) {
    switch (theme) {
      case 2 /* dark */:
        this.applyTheme(true);
        localStorage.setItem(this.storageName, "dark");
        this.cookies.set(this.storageName, "dark", this.cookieOptions);
        break;
      case 1 /* light */:
        this.applyTheme(false);
        localStorage.setItem(this.storageName, "light");
        this.cookies.set(this.storageName, "light", this.cookieOptions);
        break;
      case 0 /* system */:
        this.applyTheme(window.matchMedia("(prefers-color-scheme: dark)").matches);
        localStorage.removeItem(this.storageName);
        this.cookies.remove(this.storageName, this.cookieOptions);
    }
    this.emitChange(theme);
  }
  addChangeListener(callback) {
    this.changeListeners.push(callback);
  }
  removeChangeListener(callback) {
    const idx = this.changeListeners.indexOf(callback);
    if (idx >= 0) {
      this.changeListeners.splice(idx, 1);
    }
  }
  emitChange(theme) {
    for (const listener of this.changeListeners) {
      listener(theme);
    }
  }
  applyTheme(dark) {
    if (dark) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
  }
  get cookieOptions() {
    return {
      path: "/",
      sameSite: "strict",
      secure: true
    };
  }
};
__name(_ThemeManager, "ThemeManager");
var ThemeManager = _ThemeManager;

// src/context.tsx
import { jsx } from "react/jsx-runtime";
var DS9Context = React.createContext({
  toasts: [],
  toastHover: false,
  setToastHover: () => null,
  addToast: () => null,
  removeToast: () => null,
  updateToast: () => null,
  setToasts: () => [],
  theme: new ThemeManager("ds9_theme", false)
});
var useDS9Context = /* @__PURE__ */ __name(() => React.useContext(DS9Context), "useDS9Context");
var DS9Provider = /* @__PURE__ */ __name(({ ssr, theme, children }) => {
  const themeContext = useMemo(() => new ThemeManager(theme.storageName, ssr, theme.defaultTheme), [ssr, theme]);
  const [toasts, setToasts] = useState3([]);
  const addToast = useCallback((toast) => {
    setToasts((toasts2) => [toast, ...toasts2]);
  }, []);
  const removeToast = useCallback((id) => {
    setToasts((toasts2) => toasts2.map((t) => t.id === id ? __spreadProps(__spreadValues({}, t), { removing: true }) : t));
    setTimeout(() => {
      setToasts((toasts2) => toasts2.filter((toast) => toast.id !== id));
    }, 300);
  }, []);
  const updateToast = useCallback((id, toast) => {
    setToasts((toasts2) => toasts2.map((t) => t.id === id ? __spreadValues(__spreadValues({}, t), toast) : t));
  }, []);
  const toastContext = useMemo(
    () => ({
      toasts,
      addToast,
      removeToast,
      updateToast,
      setToasts
    }),
    [toasts, addToast, removeToast, updateToast]
  );
  const [toastHover, setToastHover] = useState3(false);
  return /* @__PURE__ */ jsx(DS9Context.Provider, { value: __spreadProps(__spreadValues({}, toastContext), { toastHover, setToastHover, theme: themeContext, ssr }), children });
}, "DS9Provider");

// src/components/toast.tsx
import { XMarkIcon } from "@heroicons/react/20/solid";

// src/components/progress.tsx
import "react";
import cn from "classnames";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var Progress = /* @__PURE__ */ __name(({ value, indeterminate, size, type, position, className }) => {
  const progressProps = {};
  if (!indeterminate) {
    progressProps.value = value;
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "aria-label": "Loading...",
      role: "progressbar",
      className: cn(
        "inline-block bg-ds9-200 dark:bg-ds9-800 overflow-hidden w-full",
        {
          "rounded-ds9": size !== "xs",
          "h-2.5": !size || size === "m",
          "h-1.5": size === "s",
          "h-0.5": size === "xs",
          relative: !position || position === "relative",
          absolute: position === "absolute"
        },
        className
      ),
      children: [
        /* @__PURE__ */ jsx2(
          "div",
          {
            className: cn("absolute inset-y-0 left-0 rounded-ds9", {
              "transition-all ease-linear duration-[250ms]": !indeterminate,
              "animate-ds9-progress-indeterminate": indeterminate,
              "bg-ds9-foreground": !type || type === "default",
              "bg-ds9-success": type === "success",
              "bg-ds9-error": type === "error",
              "bg-ds9-warning": type === "warning",
              "bg-ds9-400": type === "secondary",
              "bg-gradient-to-r from-purple-800 via-violet-900 to-purple-800": type === "accent"
            }),
            style: { width: `${indeterminate ? 100 : value != null ? value : 0}%` }
          }
        ),
        /* @__PURE__ */ jsx2("progress", __spreadProps(__spreadValues({ max: 100 }, progressProps), { className: "hidden" }))
      ]
    }
  );
}, "Progress");
var progress_default = Progress;

// src/components/spinner.tsx
import "react";
import cn2 from "classnames";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var LineSpinner = /* @__PURE__ */ __name(({ size, className }) => {
  return /* @__PURE__ */ jsx3("div", { "aria-label": "Loading...", role: "status", className, children: /* @__PURE__ */ jsxs2(
    "svg",
    {
      className: cn2("animate-spin stroke-ds9-400", {
        "h-6 w-6": !size || size === "m",
        "h-4 w-4": size === "s"
      }),
      viewBox: "0 0 256 256",
      children: [
        /* @__PURE__ */ jsx3("line", { x1: "128", y1: "32", x2: "128", y2: "64", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "24" }),
        /* @__PURE__ */ jsx3(
          "line",
          {
            x1: "195.9",
            y1: "60.1",
            x2: "173.3",
            y2: "82.7",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "24"
          }
        ),
        /* @__PURE__ */ jsx3("line", { x1: "224", y1: "128", x2: "192", y2: "128", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "24" }),
        /* @__PURE__ */ jsx3(
          "line",
          {
            x1: "195.9",
            y1: "195.9",
            x2: "173.3",
            y2: "173.3",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "24"
          }
        ),
        /* @__PURE__ */ jsx3("line", { x1: "128", y1: "224", x2: "128", y2: "192", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "24" }),
        /* @__PURE__ */ jsx3(
          "line",
          {
            x1: "60.1",
            y1: "195.9",
            x2: "82.7",
            y2: "173.3",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "24"
          }
        ),
        /* @__PURE__ */ jsx3("line", { x1: "32", y1: "128", x2: "64", y2: "128", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "24" }),
        /* @__PURE__ */ jsx3(
          "line",
          {
            x1: "60.1",
            y1: "60.1",
            x2: "82.7",
            y2: "82.7",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "24"
          }
        )
      ]
    }
  ) });
}, "LineSpinner");
var CircleSpinner = /* @__PURE__ */ __name(({ size, inverted, className }) => {
  return /* @__PURE__ */ jsx3("div", { "aria-label": "Loading...", role: "status", className, children: /* @__PURE__ */ jsxs2(
    "svg",
    {
      className: cn2("animate-spin", { "h-6 w-6": !size || size === "m", "h-4 w-4": size === "s" }),
      viewBox: "3 3 18 18",
      children: [
        /* @__PURE__ */ jsx3(
          "path",
          {
            className: cn2({
              "fill-ds9-800 dark:fill-ds9-200": !inverted,
              "fill-ds9-200 dark:fill-ds9-800": inverted
            }),
            d: "M12 5C8.13401 5 5 8.13401 5 12c0 3.866 3.13401 7 7 7 3.866.0 7-3.134 7-7 0-3.86599-3.134-7-7-7zM3 12c0-4.97056 4.02944-9 9-9 4.9706.0 9 4.02944 9 9 0 4.9706-4.0294 9-9 9-4.97056.0-9-4.0294-9-9z"
          }
        ),
        /* @__PURE__ */ jsx3(
          "path",
          {
            className: cn2({
              "fill-ds9-200 dark:fill-ds9-800": !inverted,
              "fill-ds9-800 dark:fill-ds9-200": inverted
            }),
            d: "M16.9497 7.05015c-2.7336-2.73367-7.16578-2.73367-9.89945.0-.39052.39052-1.02369.39052-1.41421.0-.39053-.39053-.39053-1.02369.0-1.41422 3.51472-3.51472 9.21316-3.51472 12.72796.0C18.7545 6.02646 18.7545 6.65962 18.364 7.05015c-.390599999999999.39052-1.0237.39052-1.4143.0z"
          }
        )
      ]
    }
  ) });
}, "CircleSpinner");
var Spinner = /* @__PURE__ */ __name((_a) => {
  var _b = _a, { variant } = _b, props = __objRest(_b, ["variant"]);
  if (variant === "line") {
    return /* @__PURE__ */ jsx3(LineSpinner, __spreadValues({}, props));
  }
  if (variant === "circle" || !variant) {
    return /* @__PURE__ */ jsx3(CircleSpinner, __spreadValues({}, props));
  }
  return null;
}, "Spinner");
var spinner_default = Spinner;

// src/components/toast.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var ToastItem = /* @__PURE__ */ __name(({
  index,
  height,
  setHeight,
  firstHeight,
  previousHeight,
  content,
  persistent,
  progress,
  removing,
  type,
  id,
  timeout
}) => {
  const { toastHover, setToastHover, removeToast } = useDS9Context();
  const [shown, setShown] = useState4(false);
  const toastRef = useRef(null);
  const timeoutRef = useRef();
  const timeoutEndRef = useRef();
  const timeoutResumeRef = useRef();
  useLayoutEffect(() => {
    var _a;
    if (toastRef.current && height === void 0) {
      height = (_a = toastRef.current) == null ? void 0 : _a.clientHeight;
      setHeight(height);
    }
  }, [height]);
  useEffect4(() => {
    setShown(true);
  }, []);
  const remove = /* @__PURE__ */ __name(() => {
    removeToast(id);
  }, "remove");
  useEffect4(() => {
    if (toastHover) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutResumeRef.current = timeoutEndRef.current ? timeoutEndRef.current - (/* @__PURE__ */ new Date()).getTime() : timeout;
        timeoutRef.current = null;
      }
    } else {
      if (timeoutRef.current)
        clearTimeout(timeoutRef.current);
      if (!persistent) {
        const remaining = timeoutResumeRef.current || timeout;
        timeoutRef.current = window.setTimeout(() => {
          removeToast(id);
        }, remaining);
        timeoutEndRef.current = (/* @__PURE__ */ new Date()).getTime() + remaining;
      }
    }
  }, [toastHover, persistent, id, removeToast, timeout]);
  const scale = 1 - index * 0.05;
  const offset = index * -20;
  firstHeight = firstHeight || 64;
  let transform = shown ? "translate3d(0px, 0, 0) scale(1)" : "translate3d(0px, 50%, 0) scale(1)";
  if (index > 0) {
    transform = toastHover ? `translate3d(0px, -${(previousHeight || 0) + 0 * index}px, -${index}px) scale(1)` : `translate3d(0px, calc(-${firstHeight}px + 100% + ${offset}px), -${index}px) scale(${scale})`;
  }
  const mouseOver = /* @__PURE__ */ __name(() => {
    setToastHover(true);
  }, "mouseOver");
  const overflow = toastHover ? index > 9 : index > 2;
  let removeClass = "hover:bg-ds9-200 dark:hover:bg-ds9-700";
  switch (type) {
    case "info":
      removeClass = "hover:bg-ds9-white hover:text-ds9-black";
      break;
    case "error":
      removeClass = "hover:bg-ds9-white hover:text-ds9-black";
      break;
  }
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      ref: toastRef,
      className: cn3("ds9-toast-item w-[420px] max-w-full absolute bottom-0 right-0 z-50 transition duration-300", {
        "opacity-0": removing || overflow || !shown && index === 0,
        "opacity-100": !removing && !overflow && (shown || index > 0),
        "pointer-events-none": overflow
      }),
      style: {
        maxWidth: `min(420px,calc(100vw - 2 * 24px))`,
        maxHeight: !toastHover && height && index > 0 ? firstHeight : void 0,
        transform
      },
      onMouseOver: mouseOver,
      onFocus: mouseOver,
      children: [
        /* @__PURE__ */ jsxs3("div", { className: "relative overflow-hidden rounded-lg shadow-ds9-sm dark:shadow-ds9-dark-sm", children: [
          type === "progress" ? /* @__PURE__ */ jsx4(
            progress_default,
            {
              position: "absolute",
              value: progress,
              indeterminate: progress === void 0,
              size: "xs",
              className: "top-0 inset-x-0",
              type: "accent"
            }
          ) : null,
          /* @__PURE__ */ jsx4(
            "div",
            {
              className: cn3("p-4 ", {
                "bg-ds9-background border border-ds9-200 dark:border-transparent": type === "default" || type === "loading" || type === "progress",
                "bg-ds9-success text-ds9-white": type === "info",
                "bg-ds9-error dark:bg-ds9-error-light text-ds9-white": type === "error"
              }),
              style: {
                maxWidth: `min(420px,calc(100vw - 2 * 24px))`,
                maxHeight: !toastHover && height && index > 0 ? firstHeight : void 0
              },
              children: /* @__PURE__ */ jsxs3("div", { className: "flex items-center justify-between gap-2", children: [
                /* @__PURE__ */ jsx4("div", { className: "flex-1 text-sm", children: content }),
                type === "loading" ? /* @__PURE__ */ jsx4(spinner_default, { size: "s" }) : null,
                type !== "loading" && type !== "progress" ? /* @__PURE__ */ jsx4("button", { type: "button", className: cn3("rounded-ds9 p-2", removeClass), onClick: remove, children: /* @__PURE__ */ jsx4(XMarkIcon, { className: "w-4 h-4" }) }) : null
              ] })
            }
          )
        ] }),
        /* @__PURE__ */ jsx4("div", { className: "h-[22px] w-full inset-x-0 top-[calc(100%-1px)]" })
      ]
    }
  );
}, "ToastItem");
var Toaster = /* @__PURE__ */ __name(() => {
  const { toasts, setToastHover } = useDS9Context();
  const [heights, setHeights] = useState4({});
  const containerRef = useRef(null);
  const orderedToasts = toasts.slice();
  let renderHeight = 0;
  const orderedHeights = orderedToasts.map((toast) => {
    const height = heights[toast.id];
    const previousHeight = renderHeight;
    if (height) {
      renderHeight += height;
    }
    return previousHeight;
  }).reverse();
  const setHeight = /* @__PURE__ */ __name((id, height) => {
    setHeights((heights2) => __spreadProps(__spreadValues({}, heights2), { [id]: height }));
  }, "setHeight");
  const topToast = orderedToasts[0];
  const firstHeight = topToast ? heights[topToast.id] : 0;
  useEffect4(() => {
    const handler = /* @__PURE__ */ __name((e) => {
      if (containerRef.current && e.target instanceof Element && !containerRef.current.contains(e.target)) {
        setToastHover(false);
      }
    }, "handler");
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [setToastHover]);
  const mouseMove = /* @__PURE__ */ __name(() => {
    setToastHover(false);
  }, "mouseMove");
  return /* @__PURE__ */ jsx4("div", { ref: containerRef, className: "fixed right-6 bottom-1 z-50", onMouseLeave: mouseMove, children: orderedToasts.reverse().map((toast, i) => {
    return /* @__PURE__ */ jsx4(
      ToastItem,
      __spreadValues({
        index: toasts.indexOf(toast),
        firstHeight,
        previousHeight: orderedHeights[i],
        height: heights[toast.id],
        setHeight: (h) => setHeight(toast.id, h)
      }, toast),
      toast.id
    );
  }) });
}, "Toaster");
var toast_default = Toaster;

// src/hooks/use_toast.ts
var useToast = /* @__PURE__ */ __name(() => {
  const { addToast, updateToast, removeToast } = useDS9Context();
  const add = useCallback2(
    (content, typeOrOpts, opts) => {
      var _a, _b;
      const id = Math.random().toString(36).substring(7);
      let finalOpts = {};
      if (typeof typeOrOpts === "string") {
        finalOpts = opts || {};
      } else {
        finalOpts = typeOrOpts || opts || {};
      }
      let type = "default";
      const timeout = (_a = finalOpts.timeout) != null ? _a : 3500;
      let persistent = (_b = finalOpts.persistent) != null ? _b : false;
      const progress = finalOpts.progress;
      if (typeof typeOrOpts === "string") {
        type = typeOrOpts;
      } else {
        type = finalOpts.type || "default";
      }
      if (type === "loading" || type === "progress") {
        persistent = true;
      }
      addToast({ id, content, type, timeout: timeout || 3500, persistent, progress });
      const update = /* @__PURE__ */ __name((updates) => {
        updateToast(id, updates);
      }, "update");
      const remove = /* @__PURE__ */ __name(() => {
        removeToast(id);
      }, "remove");
      return [update, remove];
    },
    [addToast, updateToast, removeToast]
  );
  return add;
}, "useToast");

// src/hooks/use_ssr_disabled.ts
import { useEffect as useEffect5, useState as useState5 } from "react";
var useSSRDisabled = /* @__PURE__ */ __name(() => {
  const [loaded, setLoaded] = useState5(false);
  useEffect5(() => {
    setLoaded(true);
  }, []);
  return !loaded;
}, "useSSRDisabled");

export {
  useMedia,
  use_media_default,
  spinner_default,
  useSSRDisabled,
  useDebounce,
  useTitle,
  ThemeOption,
  ThemeManager,
  DS9Context,
  useDS9Context,
  DS9Provider,
  progress_default,
  toast_default,
  useToast
};
//# sourceMappingURL=chunk-YT6WIMWL.js.map